import React, { useRef, useEffect, useState } from "react";
import { Pencil, Syringe, Thermometer } from "lucide-react";
import { esDieciocho } from "../lib/dieciocho";
import MarcaMiPAE from "./MarcaMiPAE";

// El encabezado nuevo (2026-09-08, "hagamos de nuevo el head, está feo el
// MiPAE+"). Por qué cada decisión:
// - El "+" violeta se BORRÓ del wordmark para siempre: era el único rastro
//   visual de MiPAE+ que sobrevivió al rumbo B2B, bajo el acceso abierto se
//   pintaba a TODO el mundo (no distinguía nada), y su brillo en loop
//   infinito violaba "cero confeti/premios" y no respetaba
//   prefers-reduced-motion. `isMipaePlus` sigue siendo la llave de la IA
//   ilimitada: eso no se toca, solo desaparece su rastro visual aquí.
// - La marca (las dos figuras del logo) pasa a SVG en línea con variables de
//   IDENTIDAD (`--marca-principal`/`--marca-acompanante`, definidas en
//   `:root` y `.dark` en src/index.css) en vez de un <img> con caja blanca:
//   sin la caja, el navy de la marca era casi invisible en modo oscuro:
//   necesitaba su propio par de colores claros. Son identidad de marca, no
//   ambiente: ningún tema de "Cómo se ve MiPAE" las toca (lib/tema.js).
// - El wordmark "MiPAE" va en Baloo 2 (elección del fundador 2026-09-09,
//   entre 31 candidatas), no en la serif Fraunces del cuerpo: es MARCA, no
//   ambiente, así que ningún tema ni el tipo de letra elegible la toca. La i
//   se escribe sin punto (ı) para poder animar el punto por separado.
// - A la derecha del wordmark va la misma firma manuscrita punto + trazo del
//   sello del Home (SelloAnimado.jsx), a escala del texto. Se dibuja UNA vez
//   por carga de página y queda quieta: es identidad, no premio, por eso el
//   flag de módulo (no un efecto ni estado) decide en el primer render y
//   nunca se repite aunque el lockup se desmonte y remonte navegando (el
//   header alterna entre "Volver" y el lockup constantemente).
// - Sin eslogan: "tu compañero en enfermería" sigue viviendo en el Home y la
//   landing, no se renombra ni se borra de ahí, solo no cabe aquí.
let yaAnimo = false;
// La capa se ESCRIBE letra a letra una vez por carga de página (después de que
// la firma se dibuja); al volver al Inicio ya está escrita y no se repite,
// igual que la entrada del punto y del trazo.
let yaEscribio = false;

/**
 * El nombre del modo cabe si, desplegado, termina antes de que termine el
 * hueco del header (o sea, antes del primer botón). Se mide con offsetLeft y
 * offsetWidth, que no cambian con el transform de la animación: la medida es
 * la misma esté el nombre escondido o a la vista. Sin alguna de las tres
 * piezas, no cabe.
 */
export function nombreCabe(grupo, nombre, espacio) {
  if (!grupo || !nombre || !espacio) return false;
  const derecha = grupo.getBoundingClientRect().left + nombre.offsetLeft + nombre.offsetWidth;
  return derecha <= espacio.getBoundingClientRect().right;
}

// Solo para tests: reinicia los flags de módulo entre casos.
export function _resetAnimacionLockup() {
  yaAnimo = false;
  yaEscribio = false;
}

const MS_ANTES_DE_ESCRIBIR = 1900; // tras el punto (0.9 s) y el trazo (1.15 s + 0.6 s)
const MS_POR_LETRA = 75;

// El dibujo de cada `simbolo` de lib/capaHeader.js (2026-10-16, "Solo el
// símbolo"). Un id que no está aquí no dibuja nada y la capa vuelve a
// escribirse con su palabra: fail-safe hacia nombrar, nunca hacia un hueco.
const SIMBOLOS = Object.freeze({ lapiz: Pencil, jeringa: Syringe, termometro: Thermometer });

// Sin matchMedia (jsdom, WebViews viejos) se trata como "sin movimiento": la
// palabra aparece entera. Fail-safe hacia mostrar, nunca hacia una firma vacía.
function prefiereMenosMovimiento() {
  try {
    if (typeof window.matchMedia !== "function") return true;
    return !!window.matchMedia("(prefers-reduced-motion: reduce)")?.matches;
  } catch { return true; }
}

// `capa`: {palabra, color, simbolo} de lib/capaHeader.js (capaDelPerfil), o
// null sin sesión / sin capa clara. Pedido del fundador 2026-09-14 (maqueta "La
// capa en el header"): "MiPAE.— interno", con el punto y el guión CORTOS y la
// palabra chica del color del módulo de esa capa, escrita letra a letra. Desde
// el 2026-10-16 ("Solo el símbolo", docs/pitch/preview-simbolo-header/) la capa
// con `simbolo` se DIBUJA en vez de escribirse; jefatura sigue con su palabra.
// `espacioRef`: el hueco del header a la derecha del lockup (App.jsx). Es el
// límite que el nombre del modo no puede pasar; sin él, el nombre no se
// despliega (fail-safe hacia el símbolo, jamás hacia tapar un botón).
export default function LockupHeader({ capa = null, espacioRef = null }) {
  const Simbolo = capa?.simbolo && Object.prototype.hasOwnProperty.call(SIMBOLOS, capa.simbolo)
    ? SIMBOLOS[capa.simbolo]
    : null;
  const modo = Simbolo && typeof capa.modo === "string" && capa.modo ? capa.modo : null;
  const grupoRef = useRef(null);
  const nombreRef = useRef(null);
  const [cabe, setCabe] = useState(false);
  const animarRef = useRef(!yaAnimo);
  const animar = animarRef.current;
  // Una sola lectura de la fecha por render: la estrella y el flameo del
  // trazo son la MISMA decisión, y dos llamadas podrían caer a distinto lado
  // de la medianoche del 19.
  const dieciocho = esDieciocho();
  // El flag se marca en un efecto, no en el render: mutar estado de módulo
  // durante el render es impuro y un render descartado (StrictMode, modo
  // concurrente) podría dejarlo marcado sin haber pintado nada.
  useEffect(() => {
    if (animar) yaAnimo = true;
  }, [animar]);

  // Tipeo de la capa. Sin movimiento (reduced-motion), sin matchMedia (jsdom)
  // o ya escrita en esta carga: aparece entera. Cambiar de capa en la misma
  // carga (editar el rol en Mi perfil) la reescribe.
  // Con símbolo no hay nada que tipear: la palabra vive solo en el aria-label.
  const palabra = capa?.palabra || "";
  const tipeo = Simbolo ? "" : palabra;
  const [escrito, setEscrito] = useState(() => (yaEscribio || prefiereMenosMovimiento() ? tipeo : ""));
  const [escribiendo, setEscribiendo] = useState(false);
  useEffect(() => {
    const palabra = tipeo;
    if (!palabra) { setEscrito(""); setEscribiendo(false); return undefined; }
    if (yaEscribio || prefiereMenosMovimiento()) { setEscrito(palabra); setEscribiendo(false); return undefined; }
    let i = 0;
    let intervalo = null;
    setEscrito("");
    const arranque = setTimeout(() => {
      setEscribiendo(true);
      intervalo = setInterval(() => {
        i += 1;
        setEscrito(palabra.slice(0, i));
        if (i >= palabra.length) {
          clearInterval(intervalo);
          setEscribiendo(false);
          yaEscribio = true;
        }
      }, MS_POR_LETRA);
    }, animar ? MS_ANTES_DE_ESCRIBIR : 0);
    return () => { clearTimeout(arranque); if (intervalo) clearInterval(intervalo); };
  }, [tipeo, animar]);

  // ¿Cabe el nombre del modo? Se vuelve a medir cuando cambia el ancho del
  // hueco, del grupo o del nombre (girar el teléfono, otro tamaño de letra, la
  // letra de marca que termina de cargar). Sin ResizeObserver (WebView viejo), con el
  // resize de la ventana.
  useEffect(() => {
    if (!modo) { setCabe(false); return undefined; }
    const medir = () => setCabe(nombreCabe(grupoRef.current, nombreRef.current, espacioRef?.current));
    medir();
    window.addEventListener("resize", medir);
    let observador = null;
    if (typeof ResizeObserver === "function") {
      observador = new ResizeObserver(medir);
      [grupoRef.current, nombreRef.current, espacioRef?.current].forEach((el) => el && observador.observe(el));
    }
    return () => {
      window.removeEventListener("resize", medir);
      if (observador) observador.disconnect();
    };
  }, [modo, espacioRef]);

  // La firma manuscrita del sello del Home (SelloAnimado.jsx: punto +
  // trazo), a escala del wordmark. currentColor = text-brand. Es la
  // ÚNICA excepción al veto de guiones largos ("MiPAE.—" de marca) y
  // aquí es dibujo, no texto. El trazo además menea como cola cada 9 s,
  // en fase con el salto del punto de la i (identidad, no premio, mismo
  // criterio que el meneo de Benito en PresenciaBenito). El Dieciocho
  // ese meneo se REEMPLAZA por el flameo de bandera (pedido del
  // fundador 2026-09-18): son el mismo gesto, sumarlos sería un tic.
  const firma = (
    <svg
      viewBox="0 0 78 40"
      className="ml-0.5 h-[0.55em] w-auto text-brand"
      aria-hidden="true"
      focusable="false"
      data-testid="lockup-firma"
    >
      <circle className={animar ? "animate-lockup-punto" : undefined} cx="12" cy="24" r="6" fill="currentColor" />
      <path
        className={
          (dieciocho ? "lockup-trazo-flamea" : "lockup-trazo-meneo")
          + (animar ? " animate-lockup-trazo" : "")
        }
        d="M 28 26 Q 40 22, 52 24 T 68 23"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );

  // El símbolo de la capa (2026-10-16, "Solo el símbolo"): el dibujo va en
  // lugar de la palabra, del mismo color y a escala del wordmark (mide en em,
  // crece con --texto-escala igual que la firma). Se ve desde el primer
  // momento. En la app el lockup va dentro del botón que vuelve al inicio, y
  // los hijos de un botón no se leen: ahí la capa llega al lector de pantalla
  // por el nombre del botón (App.jsx, `capaHeader`). Sin `title`: el tooltip
  // del botón dice adónde lleva el clic.
  const simbolo = palabra && Simbolo ? (
    <span
      className={"lockup-simbolo ml-1.5 self-center " + capa.color}
      role="img"
      aria-label={`Tu capa: ${palabra}`}
      data-testid="lockup-capa"
    >
      <Simbolo size="0.85em" strokeWidth={2} aria-hidden="true" focusable="false" data-testid="lockup-simbolo" />
    </span>
  ) : null;
  const despliega = cabe ? " lockup-despliega" + (animar ? " lockup-despliega-entra" : "") : "";

  return (
    <span className="inline-flex items-center gap-2.5">
      <MarcaMiPAE />
      {/* El tamaño va en el ENVOLTORIO y no solo en el texto: la firma mide
          en em y es hermana del wordmark, así hereda los mismos 20px escalados
          por --texto-escala y crece con él (sin esto heredaba los 14.4px de
          html y no escalaba con el tamaño de texto elegido). */}
      <span className="inline-flex items-baseline text-h2 leading-none">
        {/* El wordmark va en Baloo 2 (elección del fundador 2026-09-09 entre 31
            letras). Es MARCA: la clase fija la familia y no la mueve el tipo de
            letra elegible ni ningún tema. La i se escribe SIN punto (ı, U+0131) y
            el punto es un span aparte para poder animarlo; aria-label conserva la
            palabra entera para lectores de pantalla. */}
        <span className="lockup-wordmark leading-none text-brand" aria-label="MiPAE" role="img">
          <span aria-hidden="true">M</span>
          <span className="lockup-i" aria-hidden="true">
            ı
            <span
              className={
                "lockup-i-punto"
                + (animar ? " lockup-i-punto-entra" : "")
                /* El Dieciocho el punto es la estrella solitaria: cambia la
                   forma, y desde 2026-09-18 también recorre los tres colores
                   de la bandera. Las animaciones de siempre (entrada y salto)
                   se conservan: la regla del CSS las repite junto al tricolor
                   porque `animation` es una sola propiedad. */
                + (dieciocho ? " lockup-i-punto-estrella" : "")
              }
            />
          </span>
          <span aria-hidden="true">PAE</span>
        </span>
        {/* El nombre del modo (2026-10-16, "MiPAE Enfermero"): cada tanto la
            firma y el símbolo se hacen a un lado y en su lugar se lee
            "MiPAE Enfermero"; después vuelven. El nombre va encima, sin
            ocupar lugar, y solo se despliega si CABE antes de los botones
            del header (`cabe`, medido): con letra grande en un teléfono chico
            taparía la lupa, y ahí se queda solo el símbolo. El movimiento
            vive en index.css. */}
        {modo ? (
          <span ref={grupoRef} className="relative inline-flex items-baseline" data-testid="lockup-despliegue">
            <span className={"lockup-firma-simbolo inline-flex items-baseline" + despliega}>
              {firma}
              {simbolo}
            </span>
            <span
              ref={nombreRef}
              aria-hidden="true"
              className={"lockup-modo lockup-capa text-12px font-semibold leading-none " + capa.color + despliega}
              data-testid="lockup-modo"
            >
              {modo}
            </span>
          </span>
        ) : (
          <>
            {firma}
            {simbolo}
          </>
        )}
        {/* Sin símbolo (jefatura), la palabra en letra chica, escrita letra a
            letra. */}
        {palabra && !Simbolo && (
          <span
            className={"lockup-capa ml-1 text-12px font-semibold leading-none self-end pb-px " + capa.color}
            aria-label={`Tu capa: ${palabra}`}
            data-testid="lockup-capa"
          >
            <span aria-hidden="true">{escrito}</span>
            {escribiendo && <span aria-hidden="true" className="lockup-capa-cursor" />}
          </span>
        )}
      </span>
    </span>
  );
}
